import { ComponentType } from "react";

import { Optional } from "common/utils/types";

import { DeliverabilityStatus, MarketingEmailUnsubscribe } from "./inbox";

/**
 * Matches core CorePagination.get_paginated_response,
 * with max_page optionally added in clients
 * ClinicClientListCreateView._custom_paginator
 */
export interface IPaginator {
  count: number;
  page_start_index: number;
  page_end_index: number;
  page: number;
  page_size: number;
  max_page?: number;
}

/** Matches CommonProfileSerializer */
export type IProfile = {
  id: number;
  last_seen: Optional<string>;
  last_seen_display: string;
  has_completed_signup: boolean;
  is_staff: boolean;
};

/** Matches inbox/constants ClientCommunicationNumberKind */
export const ClientCommunicationNumberKind = {
  Mobile: "MOBILE",
  Home: "HOME",
  Work: "WORK",
  Other: "OTHER",
} as const;
export type ClientCommunicationNumberKind =
  (typeof ClientCommunicationNumberKind)[keyof typeof ClientCommunicationNumberKind];

// This a clinic app specific type, included in this common file to prevent
// a circular dependency
/** matches accounts ClinicClientProfileUpdateSerializer */
export interface IClinicProfileUpdate extends IBaseProfileUpdate {
  phone_number?: string;
  drivers_license_number?: string;
  drivers_license_state?: string;
  gender?: string;
  date_of_birth?: string;
}

// This a client app specific type, included in this common file to prevent
// a circular dependency
/** Matches ClientProfileUpdateSerializer & ClientUserUpdateSerializer */
export interface IClientProfileUpdate extends IBaseProfileUpdate {
  phone_number?: string;
  drivers_license_number?: string;
  drivers_license_state?: string;
  gender?: string;
  date_of_birth?: string;
}

/** Matches carts InvalidCheckoutEntity.CLIENT_USER.required_checkout_fields */
export type ClientUserMissingCheckoutField = "first_name" | "last_name" | "primary_contact_number";

/** matches ClientUserSerializer */
export interface ClientUser {
  id: number;
  drivers_license_number: Optional<string>;
  drivers_license_state: Optional<string>;
  gender: Optional<ProfileGender>;
  date_of_birth: Optional<string>;
  full_name: string;
  first_name: Optional<string>;
  last_name: Optional<string>;
  initials: string;
}

export interface BaseExternalContact {
  id: number;
  name: string;
}

/** matches clients BasicClientCommunicationNumberSerializer */
export interface ClientCommunicationNumber {
  id: number;
  number: number;
  number_display: string;
  sms_enabled: boolean;
  kind: Optional<ClientCommunicationNumberKind>;
  kind_display: Optional<string>;
  custom_kind: Optional<string>;
}

export interface ClientCommunicationNumberWithIsAuth extends ClientCommunicationNumber {
  is_auth_method: boolean;
  is_unsubscribed_from_marketing_sms: boolean;
  is_hard_unsubscribed_from_marketing_sms: boolean;
  deliverability_status: DeliverabilityStatus;
  client_user: ClientUser | null;
  external_contact: BaseExternalContact | null;
  client_communication_method: number;
}

/** matches clients BasicClientEmailAddressSerializer */
export interface ClientEmailAddress {
  id: number;
  email: string;
}

/** Matches ClientEmailAddressWithIsAuthSerializer */
export interface ClientEmailAddressWithIsAuth extends ClientEmailAddress {
  is_auth_method: boolean;
  deliverability_status: DeliverabilityStatus;
  client_user: ClientUser | null;
  external_contact: BaseExternalContact | null;
  client_communication_method: number;
  is_unsubscribed_from_marketing_emails: boolean;
  marketing_email_unsubscribe: MarketingEmailUnsubscribe | null;
}
/** matches ClientUserWithContactsSerializer */
export interface ClientUserWithContacts {
  id: number;
  drivers_license_number: Optional<string>;
  drivers_license_state: Optional<string>;
  gender: Optional<ProfileGender>;
  date_of_birth: Optional<string>;
  full_name: string;
  first_name: Optional<string>;
  last_name: Optional<string>;
  initials: string;
  primary_contact_number: ClientCommunicationNumber | null;
  primary_contact_email: ClientEmailAddress | null;
  missing_checkout_fields: ClientUserMissingCheckoutField[];
  relationship_kind: Optional<CoOwnershipRelationshipKind>;
  relationship_kind_display: Optional<string>;
  custom_relationship: Optional<string>;
}

/** Matches CoOwnershipRelationshipKind */
export const CoOwnershipRelationshipKind = {
  SPOUSE: "SPOUSE",
  PARTNER: "PARTNER",
  PARENT: "PARENT",
  CHILD: "CHILD",
  SIBLING: "SIBLING",
  OTHER: "OTHER",
} as const;
export type CoOwnershipRelationshipKind =
  (typeof CoOwnershipRelationshipKind)[keyof typeof CoOwnershipRelationshipKind];

/** Matches BaseProfileUpdateSerializer */
export interface IBaseProfileUpdate {
  first_name?: string;
  last_name?: string;
  email?: string;
}

/** Matches CommonDoctorSerializer */
export type Doctor = {
  id: number;
  first_name: string;
  last_name: string;
  name_display: string;
  credentials: DoctorCredentialKind[];
  image_url: string;
};

/** Matches clinics.constants DoctorCredentialKind */
export const DoctorCredentialKind = {
  DVM: "DVM",
  VMD: "VMD",
} as const;
export type DoctorCredentialKind = (typeof DoctorCredentialKind)[keyof typeof DoctorCredentialKind];

/** Matches core.constants PatientKind */
export const PatientKind = {
  Canine: "CANINE",
  Feline: "FELINE",
  Equine: "EQUINE",
  Avian: "AVIAN",
  Bovine: "BOVINE",
  Ferret: "FERRET",
  Rabbit: "RABBIT",
  Rodent: "RODENT",
  Reptile: "REPTILE",
  Amphibian: "AMPHIBIAN",
  Porcine: "PORCINE",
  Caprine: "CAPRINE",
  Fish: "FISH",
  Sheep: "SHEEP",
  Camelid: "CAMELID",
  Ratite: "RATITE",
  Marsupial: "MARSUPIAL",
  Mustelid: "MUSTELID",
  Cervid: "CERVID",
  Procyonidae: "PROCYONIDAE",
  Erinaceidae: "ERINACEIDAE",
  Mephitidae: "MEPHITIDAE",
  Other: "OTHER",
  Unspecified: "UNSPECIFIED",
} as const;
export type PatientKind = (typeof PatientKind)[keyof typeof PatientKind];

export const PatientKindDisplayNames: Record<PatientKind, string> = {
  [PatientKind.Canine]: "Canine",
  [PatientKind.Feline]: "Feline",
  [PatientKind.Equine]: "Equine",
  [PatientKind.Avian]: "Avian",
  [PatientKind.Bovine]: "Bovine",
  [PatientKind.Ferret]: "Ferret",
  [PatientKind.Rabbit]: "Rabbit",
  [PatientKind.Rodent]: "Rodent",
  [PatientKind.Reptile]: "Reptile",
  [PatientKind.Amphibian]: "Amphibian",
  [PatientKind.Fish]: "Fish",
  [PatientKind.Porcine]: "Porcine",
  [PatientKind.Caprine]: "Caprine",
  [PatientKind.Sheep]: "Sheep",
  [PatientKind.Camelid]: "Camelid",
  [PatientKind.Ratite]: "Ratite",
  [PatientKind.Marsupial]: "Marsupial",
  [PatientKind.Mustelid]: "Mustelid",
  [PatientKind.Cervid]: "Cervid",
  [PatientKind.Procyonidae]: "Procyonidae",
  [PatientKind.Erinaceidae]: "Erinaceidae",
  [PatientKind.Mephitidae]: "Mephitidae",
  [PatientKind.Other]: "Other",
  [PatientKind.Unspecified]: "Unspecified",
};

/** Matches clients PatientStatus */
export const PatientStatus = {
  Active: "ACTIVE",
  Inactive: "INACTIVE",
  Deceased: "DECEASED",
} as const;
export type PatientStatus = (typeof PatientStatus)[keyof typeof PatientStatus];

/** Matches clients ClientStatus */
export const ClientStatus = {
  Active: "ACTIVE",
  Inactive: "INACTIVE",
} as const;
export type ClientStatus = (typeof ClientStatus)[keyof typeof ClientStatus];

/** Values of the backend PatientColor enum */
export type PatientColorValue = string;

/** Matches clients PatientColorData */
export type PatientColor = {
  value: PatientColorValue;
  label: string;
};

/** Matches core.constants SexKind */
export const SexKind = {
  Male: "MALE",
  Female: "FEMALE",
  Unknown: "UNKNOWN",
} as const;
export type SexKind = (typeof SexKind)[keyof typeof SexKind];

/** Matches core.constants ProfileGender */
export const ProfileGender = {
  Male: "MALE",
  Female: "FEMALE",
} as const;
export type ProfileGender = (typeof ProfileGender)[keyof typeof ProfileGender];

/** matches WeightUnitOfMeasure */
export const WeightUnitOfMeasure = {
  Pound: "POUND",
  Kilogram: "KILOGRAM",
  Gram: "GRAM",
  Ounce: "OUNCE",
} as const;
export type WeightUnitOfMeasure = (typeof WeightUnitOfMeasure)[keyof typeof WeightUnitOfMeasure];

/** Matches carts InvalidCheckoutEntity.PATIENT.required_checkout_fields */
export type PatientMissingCheckoutField = Partial<keyof IPatientWrite>;

/** Matches clients CommonBasicPatientSerializer */
export type IPatient = {
  id: number;
  ui_string: string;
  description: string;
  /** pet's name: may be blank so `name_display` is recommended for reading in
   *  most contexts */
  name: string;
  name_display: string;
  weight_in_units: number | null;
  weight_unit_of_measure: WeightUnitOfMeasure | "" | null;
  weight_display: Optional<string>;
  birth_day: Optional<number>;
  birth_month: Optional<number>;
  birth_year: Optional<number>;
  deceased_date: Optional<string>;
  species: PatientKind;
  custom_species: Optional<string>;
  species_display: string;
  primary_breed: Optional<PatientBreed>;
  secondary_breeds: PatientBreed[];
  color: Optional<PatientColor>;
  custom_color: Optional<string>;
  custom_breed: Optional<string>;
  sex: SexKind;
  sex_display: string;
  allergies: string;
  age_display: Optional<string>;
  // TODO [shared-ownership]: Check consumers to see which should account for
  // inactive client relationship
  is_active: boolean;
  is_alive: boolean;
  missing_checkout_fields: PatientMissingCheckoutField[];
  client_id: number;
  universal_patient: number | null;
};

/** Matches clients PatientBreedData */
export type PatientBreed = {
  value: PatientBreedValue;
  label: string;
  species: PatientKind;
};

/** Values of the backend PatientBreed enum */
export type PatientBreedValue = string;

/** matches accounts AddressShippabilityStatus */
export const AddressShippabilityStatus = {
  Active: "ACTIVE",
  Inactive: "INACTIVE",
  Undeliverable: "UNDELIVERABLE",
  CannotFulfillItem: "CANNOT_FULFILL_ITEM",
} as const;
export type AddressShippabilityStatus =
  (typeof AddressShippabilityStatus)[keyof typeof AddressShippabilityStatus];

/** Matches accounts CommonAddressSerializer */
export interface IAddress {
  id: number;
  name: string;
  street: string;
  address_one: string;
  address_two: string;
  city: string;
  state: string;
  county: string;
  zipcode: string;
  country: string;
  phone_number: Optional<number>;
  ui_string: string;
  is_shippable: boolean;
}

export interface IGeoAddress {
  address_one: string;
  address_two: string;
  city: string;
  state: string;
  county: string;
  zipcode: string;
  country: string;
}

/** Geographic coordinate pair in the form of [longitude, latitude] */
export type Coordinate = [number, number];

export const PaymentProvider = {
  MASTERCARD: "mastercard",
  VISA: "visa",
  AMEX: "amex",
  DISCOVER: "discover",
} as const;
export type PaymentProvider = (typeof PaymentProvider)[keyof typeof PaymentProvider];

/** Matches payments StripeWalletTypes */
export const WalletType = {
  APPLE: "APPLE",
  GOOGLE: "GOOGLE",
  SAMSUNG: "SAMSUNG",
  MASTERPASS: "MASTERPASS",
  AMEX_EXPRESS: "AMEX_EXPRESS",
  VISA_CHECKOUT: "VISA_CHECKOUT",
} as const;
export type WalletType = (typeof WalletType)[keyof typeof WalletType];

/** Matches payments PaymentMethodChargeabilityStatus */
export const PaymentMethodChargeabilityStatus = {
  Valid: "VALID",
  Expired: "EXPIRED",
  Deleted: "DELETED",
} as const;
export type PaymentMethodChargeabilityStatus =
  (typeof PaymentMethodChargeabilityStatus)[keyof typeof PaymentMethodChargeabilityStatus];

export interface CardPaymentMethod {
  id: number;
  is_klarna: false;
  // Interac debit tapped on a card reader has no reusable card behind it, so brand,
  // last_four, and expiration come back null. Model them nullable so every consumer
  // guards the missing values instead of rendering "null" or throwing.
  last_four: string | null;
  brand: PaymentProvider | null;
  name: string;
  wallet: WalletType | "";
  expiration_month: string | null;
  expiration_year: string | null;
  ui_string: string;
  alt_ui_string: string;
  short_ui_string: string;
  chargeability_status: PaymentMethodChargeabilityStatus;
  is_chargeable: boolean;
}

export interface KlarnaPaymentMethod {
  id: number;
  is_klarna: true;
  last_four: null;
  brand: null;
  name: string;
  wallet: "";
  expiration_month: null;
  expiration_year: null;
  ui_string: string;
  alt_ui_string: string;
  short_ui_string: string;
  chargeability_status: PaymentMethodChargeabilityStatus;
  is_chargeable: boolean;
}

export interface KlarnaPaymentMethodPlaceholder {
  id: "__klarna__";
  label: string;
  is_klarna: true;
  wallet: "";
}

export interface WalletPaymentMethodPlaceholder {
  id: "__wallet__";
  wallet: WalletType;
  is_klarna: false;
}

export type PaymentMethodOption =
  | PaymentMethod
  | KlarnaPaymentMethodPlaceholder
  | WalletPaymentMethodPlaceholder;

/** Matches payments CommonPaymentMethodSerializer */
export type PaymentMethod = CardPaymentMethod | KlarnaPaymentMethod;

/** matches PaymentDeclineErrorData */
export type CardPaymentDeclineError = {
  card_decline_description: string;
  card_decline_next_steps: string;
};

/** Matches StripeCardDeclineCodeData */
export type StripeCardDeclineCode = {
  decline_code: string;
  description: string;
  next_steps: string;
};

/** Matches payments CommonClientSecretSerializer */
export interface ISetupIntent {
  client_secret: string;
}

// From app/subscriptions/constants
export const FrequencyUnit = {
  Day: "DAY",
  Week: "WEEK",
  Month: "MONTH",
  Year: "YEAR",
} as const;
export type FrequencyUnit = (typeof FrequencyUnit)[keyof typeof FrequencyUnit];

/** Matches subscriptions types.SubscriptionFrequency */
export interface SubscriptionFrequency {
  frequency: number;
  interval_unit: FrequencyUnit;
  display: string;
}

export type FullSortDirective<S extends string> = [S, "asc" | "desc"];

/** Either a [key, sort direction] tuple, or a single key to sort on. If no direction is provided, ascending is assumed */
export type SortDirective<S extends string> = FullSortDirective<S> | S;

export interface ListSearch {
  page: number;
  page_size: number;
  sort: string;
  q: string;
}

export type ImportStatement = () => Promise<{ default: ComponentType<any> }>;

/** Matches subscriptions UserSubscriptionAction */
export const UserSubscriptionAction = {
  Modify: "MODIFY",
  Resume: "RESUME",
  Cancel: "CANCEL",
  OrderNow: "ORDER_NOW",
  Skip: "SKIP",
  ChangeNextScheduleDate: "CHANGE_NEXT_SCHEDULE_DATE",
} as const;
export type UserSubscriptionAction =
  (typeof UserSubscriptionAction)[keyof typeof UserSubscriptionAction];

/** Matches subscriptions UISubscriptionStatus */
export const UISubscriptionStatus = {
  Active: "ACTIVE",
  Canceled: "CANCELED",
  Paused: "PAUSED",
  FailedPayment: "FAILED_PAYMENT",
  Imported: "IMPORTED",
} as const;
export type UISubscriptionStatus = (typeof UISubscriptionStatus)[keyof typeof UISubscriptionStatus];

export const ItemSelectionKind = {
  Flavor: "FLAVOR",
} as const;
export type ItemSelectionKind = (typeof ItemSelectionKind)[keyof typeof ItemSelectionKind];

/** Matches catalog BasicCatalogImageSerializer */
export interface BasicCatalogImage {
  id: number;
  url: string;
  thumbnail_url: string;
  medium_url: string;
  large_url: string;
}

/** Matches catalog ProductLineGuaranteedAnalysisItemSerializer */
export type ProductLineGuaranteedAnalysisItem = {
  id: number;
  nutrient: string;
  minmax: string;
  amount: string;
};

/** Matches catalog BasicFlavorSelectionSerializer */
export interface FlavorSelection {
  id: number;
  display: string;
  value: string;
}

export type GroupedItemFlavors = {
  label: string;
  required: boolean;
  options: FlavorSelection[];
};

/** Matches catalog BasicItemSerializer */
export interface BasicItem {
  id: number;
  product_id: number;
  name: string;
  duration_unit: FrequencyUnit;
  duration_value: number;
  msrp: number;
  full_name: string;
  image: Optional<BasicCatalogImage>;
  product_line_relative_name: string;
  weight: number;
  recommended_frequency: SubscriptionFrequency;
  vetcove_item_url: string;
  eligible_for_autoship_discounts: boolean;
  is_backordered: boolean;
  primary_label: Optional<string>;
  secondary_label: Optional<string>;
  days_supply: number;
  is_preventative: boolean;
  subscription_frequency_options: SubscriptionFrequency[];
}

/** Matches catalog BasicItemAndFlavorOptionsSerializer */
export interface BasicItemAndFlavorOptions extends BasicItem {
  flavor_options: GroupedItemFlavors[];
}

/** Matches catalog CommonItemWithProductSerializer */
export interface IItemWithProduct extends BasicItem {
  product: IItemProduct;
}

/** Matches catalog CommonItemAndFlavorOptionsWithProductSerializer */
export interface IItemAndFlavorOptionsWithProduct extends BasicItemAndFlavorOptions {
  product: IItemProduct;
}

/** Matches catalog CommonBasicProductSerializer */
export interface IItemProduct {
  id: number;
  product_line: IItemProductLine;
  name: string;
  default_sig: string | null;
}

/** Matches catalog BasicProductLineSerializer */
export interface IItemProductLine {
  id: number;
  name: string;
  brand_name: string;
  description: string;
  short_description: string;
  manufacturer: BasicManufacturer;
  categories: BasicCategory[];
  primary_category_name: string;
  prescription_required: boolean;
  refrigeration_required: boolean;
  flexscriptions_enabled: boolean;
  is_controlled: boolean;
  is_eligible_for_prn: boolean;
  is_compound: boolean;
}

export type TableProductLine = {
  name: string;
};

export type TableProduct = {
  product_line: TableProductLine;
};

/** Matches catalog BasicManufacturerSerializer */
export interface BasicManufacturer {
  id: number;
  name: string;
}

/**
 * Minimal category shape sent by inline BE dicts that don't use BasicCategorySerializer
 */
export type BasicCategorySummary = {
  id: number;
  name: string;
};

/** Matches catalog BasicCategorySerializer */
export interface BasicCategory {
  id: number;
  name: string;
  slug: string;
  level: CategoryLevel;
  parent: Optional<number>;
  image: Optional<string>;
  icon: CategoryIcon;
}

/** matches catalog CategoryWithParentSerializer */
export interface CategoryWithParent extends BasicCategory {
  parent_name: string;
}

/** Matches catalog/constants.py */
export const CategoryLevel = {
  Primary: "PRIMARY",
  Secondary: "SECONDARY",
} as const;
export type CategoryLevel = (typeof CategoryLevel)[keyof typeof CategoryLevel];

/** Matches catalog/constants.py */
export const CategoryIcon = {
  Compound: "COMPOUND",
  Drug: "DRUG",
  Food: "FOOD",
  Preventative: "PREVENTATIVE",
  Supplement: "SUPPLEMENT",
  Other: "OTHER",
} as const;
export type CategoryIcon = (typeof CategoryIcon)[keyof typeof CategoryIcon];

/** matches uploads UploadedImageSerializer */
export type UploadedImage = {
  id: number;
  original_url: string;
  thumbnail_url: string;
  medium_url: string;
  large_url: string;
};

/** Matches POST to ClinicUploadedImageCleanView */
export type UploadedImageCleanBody = {
  uploaded_image: number;
};

/** Matches clients CommonBasicPatientSerializer _writable_fields */
export type IPatientWrite = {
  name: string;
  sex: SexKind;
  weight_in_units: number | null;
  weight_unit_of_measure: WeightUnitOfMeasure | "";
  birth_day: number | null;
  birth_month: number | null;
  birth_year: number | null;
  deceased_date?: string | null;
  species: PatientKind;
  custom_species: string | null;
  color: PatientColor | null;
  /** Free text used by surfaces that let a patient keep a color the picker doesn't offer. */
  custom_color?: string;
  allergies: string;
  // matches read typing for easy optimistic updates
  primary_breed: PatientBreed | null;
  secondary_breeds: PatientBreed[];
  custom_breed: string;
  status: PatientStatus;
  image_id: number | null;
};

/** Matches clients CommonBasicPatientSerializer _writable_fields */
export type IPatientWriteBody = Omit<
  IPatientWrite,
  "primary_breed" | "secondary_breeds" | "color"
> & {
  primary_breed: PatientBreedValue | "";
  secondary_breeds: PatientBreedValue[];
  color: PatientColorValue | "";
};

/** matches clients ClientUpdateSerializer */
export type IClientUpdateFields = {
  primary_address?: number;
  primary_payment_method?: number;
  primary_email_address?: string;
  primary_communication_number?: number;
};

export type ClientAccountSettingsUpdateFields = {
  primary_client_user?: number;
  primary_address?: number;
  primary_payment_method?: number;
  primary_email_address?: number;
  primary_communication_number?: number;
};

/** matches reviews ReviewProductLineSerializer */
export interface IReviewProductLine {
  id: number;
  name: string;
  manufacturer: BasicManufacturer;
  primary_category: BasicCategory;
  image?: BasicCatalogImage;
}

export type ReviewRating = 1 | 2 | 3 | 4 | 5;

/**
 * matches clinics features_controller get_features returned key values
 * and clinic constant FeatureKind
 */
export const Feature = {
  PreventativesCompliance: "PREVENTATIVES_COMPLIANCE",
  Fulfillments: "FULFILLMENTS",
  Shortcodes: "SHORTCODES",
  ItemPickups: "ITEM_PICKUPS",
  Marketing: "MARKETING",
  Onboarding: "ONBOARDING",
  TrainingDocs: "TRAINING_DOCS",
  Rebates: "REBATES",
  StoreAnalytics: "STORE_ANALYTICS",
  LocationGroups: "LOCATION_GROUPS",
  Etailers: "ETAILERS",
  Procurement: "PROCUREMENT",
  Inbox: "INBOX",
  AutoReply: "AUTO_REPLY",
  PimsAutoReply: "PIMS_AUTO_REPLY",
  SameDayDelivery: "SAME_DAY_DELIVERY",
  ZoetisPetcareRewards: "ZOETIS_PETCARE_REWARDS",
  CarouselV2: "CAROUSEL_V2",
  ProductCardAddToCart: "PRODUCT_CARD_ADD_TO_CART",
  FreeShippingProgress: "FREE_SHIPPING_PROGRESS",
  ClinicUserProfileImages: "CLINIC_USER_PROFILE_IMAGES",
  RxRequiresPrescriberPincode: "RX_REQUIRES_PRESCRIBER_PINCODE",
  CUSTOM_PERMISSIONS: "CUSTOM_PERMISSIONS",
  ImportedRxDeclineReason: "IMPORTED_RX_DECLINE_REASON",
  TreatmentDeclineReason: "TREATMENT_DECLINE_REASON",
  StorefrontQrCode: "STOREFRONT_QR_CODE",
  Klarna: "KLARNA",
  PromotionalMarketingEmailPreviews: "PROMOTIONAL_MARKETING_EMAIL_PREVIEWS",
  OnboardingRxActiveOnlyFilters: "ONBOARDING_RX_ACTIVE_ONLY_FILTERS",
  ClinicClientMergeTool: "CLINIC_CLIENT_MERGE_TOOL",
  ClinicIncentiveCreateV2: "CLINIC_INCENTIVE_CREATE_V2",
  SigValidator: "SIG_VALIDATOR",
  RxInitials: "RX_INITIALS",
  Employees: "EMPLOYEES",
  Collaterals: "COLLATERALS",
  TaylorCollateralOrders: "TAYLOR_COLLATERAL_ORDERS",
  PayoutsPage: "PAYOUTS_PAGE",
  ExternalPrescriptionRequest: "EXTERNAL_PRESCRIPTION_REQUEST",
  DraftsPrescriptionDashboard: "DRAFTS_PRESCRIPTION_DASHBOARD",
  BulkPricingUpload: "BULK_PRICING_UPLOAD",
  LoyaltyPrograms: "LOYALTY_PROGRAMS",
  BusinessDetails: "BUSINESS_DETAILS",
  SingleDispenseFeeInclusion: "SINGLE_DISPENSE_FEE_INCLUSION",
  TaxDocumentsRequired: "TAX_DOCUMENTS_REQUIRED",
  SchedulerBookingV2: "SCHEDULER_BOOKING_V2",
  AutoshipEDB: "AUTOSHIP_EDB",
  AutoshipFrequencySchedule: "AUTOSHIP_FREQUENCY_SCHEDULE",
  AutoshipFailedPaymentNeedingAction: "AUTOSHIP_FAILED_PAYMENT_NEEDING_ACTION",
  AutoshipClientBookingLink: "AUTOSHIP_CLIENT_BOOKING_LINK",
  InvoiceAutoshipPrompts: "INVOICE_AUTOSHIP_PROMPTS",
  AllowStandardInventoryNameReuse: "ALLOW_STANDARD_INVENTORY_NAME_REUSE",
  PaymentElementHD: "PAYMENT_ELEMENT_HD",
  ClientAssist: "CLIENT_ASSIST",
  SipResolutionV2: "SIP_RESOLUTION_V2",
  ControlledSubstanceShippingAddressBasis: "CONTROLLED_SUBSTANCE_SHIPPING_ADDRESS_BASIS",
  ControlledSubstanceGranularControls: "CONTROLLED_SUBSTANCE_GRANULAR_CONTROLS",
  CarouselsClinicUi: "CAROUSELS_CLINIC_UI",
  ClinicRefundClientOrder: "CLINIC_REFUND_CLIENT_ORDER",
  DraftsOrdersDashboard: "DRAFTS_ORDERS_DASHBOARD",
  PimsLocationTags: "PIMS_LOCATION_TAGS",
  AuthorizedPrescriberCategoryLimits: "AUTHORIZED_PRESCRIBER_CATEGORY_LIMITS",
  // Pims features
  EodManualDiscounts: "EOD_MANUAL_DISCOUNTS",
  TeamChat: "TEAM_CHAT",
  TeamChatMultiLocation: "TEAM_CHAT_MULTI_LOCATION",
  Scheduler: "SCHEDULER",
  ShiftScheduler: "SHIFT_SCHEDULER",
  ClientAppointmentCancelation: "CLIENT_APPOINTMENT_CANCELATION",
  MedicalRecords: "MEDICAL_RECORDS",
  CarePlans: "CARE_PLANS",
  Inventory: "INVENTORY",
  Forms: "FORMS",
  PatientWeightTracking: "PATIENT_WEIGHT_TRACKING",
  PimsCommunications: "PIMS_COMMUNICATIONS",
  CoOwnerPhoneMatching: "CO_OWNER_PHONE_MATCHING",
  CoOwnerEmailMatching: "CO_OWNER_EMAIL_MATCHING",
  FecalSampleReminder: "FECAL_SAMPLE_REMINDER",
  SpellCorrect: "SPELL_CORRECT",
  SmartEdit: "SMART_EDIT",
  SmartCompose: "SMART_COMPOSE",
  DrugMonograph: "DRUG_MONOGRAPH",
  WorkInProgress: "WORK_IN_PROGRESS",
  ClinicTheme: "CLINIC_THEME",
  RichEditorDebugging: "RICH_EDITOR_DEBUGGING",
  PatientNoteDocumentationKind: "PATIENT_NOTE_DOCUMENTATION_KIND",
  PurchaseOrders: "PURCHASE_ORDERS",
  GlobalSearchV2: "GLOBAL_SEARCH_V2",
  CachedFormValues: "CACHED_FORM_VALUES",
  RealtimeEvents: "REALTIME_EVENTS",
  RealtimePresence: "REALTIME_PRESENCE",
  ReminderMappingV2: "REMINDER_MAPPING_V2",
  StopReminderMapping: "STOP_REMINDER_MAPPING",
  ControlledSubstanceLogsV2: "CONTROLLED_SUBSTANCE_LOGS_V2",
  CustomInventoryManagement: "CUSTOM_INVENTORY_MANAGEMENT",
  InventoryCounts: "INVENTORY_COUNTS",
  Hospitalizations: "HOSPITALIZATIONS",
  Boarding: "BOARDING",
  BoardingConfig: "BOARDING_CONFIG",
  BoardingQuotes: "BOARDING_QUOTES",
  PrescriptionLabelRxRefillQr: "PRESCRIPTION_LABEL_RX_REFILL_QR",
  ReadOnlyMode: "READ_ONLY_MODE",
  SystemStatus: "SYSTEM_STATUS",
  CompassionProgram: "COMPASSION_PROGRAM",
  CarePlanInvoiceSeparation: "CARE_PLAN_INVOICE_SEPARATION",
  CarePlanTerminationFeeV2: "CARE_PLAN_TERMINATION_FEE_V2",
  Vetline: "VETLINE",
  StorefrontHomepageLink: "STOREFRONT_HOMEPAGE_LINK",
  PimsLiveSupport: "PIMS_LIVE_SUPPORT",
  PaymentLinks: "PAYMENT_LINKS",
  PaymentElementPIMS: "PAYMENT_ELEMENT_PIMS",
  ControlledSubstanceGroupConfig: "CONTROLLED_SUBSTANCE_GROUP_CONFIG",
  ServiceMinimumSubtotalPricing: "SERVICE_MINIMUM_SUBTOTAL_PRICING",
  EstimateTemplatePricing: "ESTIMATE_TEMPLATE_PRICING",
  ServiceDefaultDurations: "SERVICE_DEFAULT_DURATIONS",
  UseClinicTimezone: "USE_CLINIC_TIMEZONE",
  HdUiV2: "HD_UI_V2",
  ArUploadUi: "AR_UPLOAD_UI",
  SegmentManagement: "SEGMENT_MANAGEMENT",
  IncentiveSegmentTargeting: "INCENTIVE_SEGMENT_TARGETING",
  ClientEngagementBroadcast: "CLIENT_ENGAGEMENT_BROADCAST",
  ShoppingLists: "SHOPPING_LISTS",
  ApiKeysAccessForHdIndependentClinic: "API_KEYS_ACCESS_FOR_HD_INDEPENDENT_CLINIC",
  InvoiceLevelNotRendered: "INVOICE_LEVEL_NOT_RENDERED",
  InvoiceItemNotRenderedWithNoRefund: "INVOICE_ITEM_NOT_RENDERED_WITH_NO_REFUND",
  SelfServiceBulkInventory: "SELF_SERVICE_BULK_INVENTORY",
  EstimateClientApprovalLink: "ESTIMATE_CLIENT_APPROVAL_LINK",
  MedicalRecordSendLink: "MEDICAL_RECORD_SEND_LINK",
  StripeDisputesManagement: "STRIPE_DISPUTES_MANAGEMENT",
  TriageMessaging: "TRIAGE_MESSAGING",
  UniversalPatientTimeline: "UNIVERSAL_PATIENT_TIMELINE",
  ClientMobileAppointmentConfirmation: "CLIENT_MOBILE_APPOINTMENT_CONFIRMATION",
  PreAppointmentSummary: "PRE_APPOINTMENT_SUMMARY",
  ClientVisitSummary: "CLIENT_VISIT_SUMMARY",
  ClinicVisitSummary: "CLINIC_VISIT_SUMMARY",
  ClientLabSummary: "CLIENT_LAB_SUMMARY",
  ClinicLabSummary: "CLINIC_LAB_SUMMARY",
  ObtV3: "OBT_V3",
  ObtClientAutoLink: "OBT_CLIENT_AUTO_LINK",
  DosageReminders: "DOSAGE_REMINDERS",
  ChargeCapture: "CHARGE_CAPTURE",
  SplitCatalogListView: "SPLIT_CATALOG_LIST_VIEW",
  DocumentSummaryAutoGeneration: "DOCUMENT_SUMMARY_AUTO_GENERATION",
} as const;
export type Feature = (typeof Feature)[keyof typeof Feature];

export type Features = Record<Feature, boolean>;

export interface IPaginatedQuery<T> {
  paginator: IPaginator;
  results: T[];
}

/**
 * Matches a subset of backend EmailUnsubscribeReason (inbox/constants.py).
 * The backend enum includes additional non-user-facing values
 */
export const UnsubscribeReason = {
  TooManyNotifications: "TOO_MANY_NOTIFICATIONS",
  NoLongerClient: "NO_LONGER_CLIENT",
  NoLongerHasPets: "NO_LONGER_HAS_PETS",
  DoesNotUseStorefront: "DOES_NOT_USE_STOREFRONT",
  Other: "OTHER",
} as const;
export type UnsubscribeReason = (typeof UnsubscribeReason)[keyof typeof UnsubscribeReason];

export type OptimisticCreate = {
  optimistic_id: number;
};

/** Matches the BundleRenderType enum defined in general/constants.py */
export const BundleRenderType = {
  EMBED: "embed",
  FULL: "full",
} as const;
export type BundleRenderType = (typeof BundleRenderType)[keyof typeof BundleRenderType];

/**
 * Matches `SubscriptionCancelReason` in `app/subscriptions/constants.py`.
 */
export const SubscriptionCancelReason = {
  // -- Client and clinic-facing cancel reasons -- #
  AdverseReaction: "ADVERSE_REACTION",
  ChangeInTreatment: "CHANGE_IN_TREATMENT",
  ExcessProduct: "EXCESS_PRODUCT",
  Expensive: "EXPENSIVE",
  NoLongerClientOfClinic: "NO_LONGER_CLIENT_OF_CLINIC",
  Other: "OTHER",
  PatientInactive: "PATIENT_INACTIVE",
  PetPassedAway: "PET_PASSED_AWAY",
  SubscriptionNotWanted: "SUBSCRIPTION_NOT_WANTED",
} as const;
export type SubscriptionCancelReason =
  (typeof SubscriptionCancelReason)[keyof typeof SubscriptionCancelReason];

export type SubscriptionCancelReasonOption = {
  value: SubscriptionCancelReason;
  label: string;
};
